import React, { useCallback, useEffect, useLayoutEffect, useMemo, } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { AppView } from 'design-system/AppView';
import StatusBar from 'design-system/StatusBar';
import { Indicator } from 'design-system/Indicator';
import SectionHeader from 'design-system/SectionHeader';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectShowSubscriptionPaymentFailed, selectShowSeatBasedSubscriptionPaymentFailed, } from 'features/premium/selectors';
import { selectUserTier } from 'features/premium/selectors/tier';
import { benefits } from 'features/premium/benefits';
import { NordVPNCard } from 'features/NordVpn/NordVPNCard';
import { getVPNStatus } from 'features/NordVpn/actions';
import FraudDetectionCard from 'features/feed/components/FraudDetectionCard';
import SeatBasedSubscriptionPaymentFailed from 'features/feed/components/needs-fix/SeatBasedSubscriptionPaymentFailed';
import SubscriptionPaymentFailed from 'features/feed/components/needs-fix/SubscriptionPaymentFailed';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useIsMainAppWalkthroughV6Active } from 'features/feed/components/walkthrough/hooks/useIsMainAppWalkthroughV6Active';
import WalkthroughHeaderOverlay from 'features/feed/components/walkthrough/WalkthroughHeaderOverlay';
import Walkthrough from 'features/feed/components/walkthrough/Walkthrough';
import useWalkthroughLayout from 'features/feed/components/walkthrough/useWalkthroughLayout';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useShowWalkthroughV6 } from 'features/feed/components/walkthrough/hooks/useShowWalkthroughV6';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import { IconSettings } from 'design-system/icons/IconSettings';
import { Spacer } from 'design-system';
import { isUnlocked } from 'utils';
import { IconHelp } from 'design-system/icons/IconHelp';
import { AccountSettingsHeader } from './AccountSettingsHeader';
import { isWeb } from '../../../constants';
const NordVPNSection = () => {
    const dispatch = useAppDispatch();
    const userTier = useAppSelector(selectUserTier);
    const isNordVPNAvailable = isUnlocked(userTier, benefits.nordVPN.minTier);
    useEffect(() => {
        if (isNordVPNAvailable) {
            dispatch(getVPNStatus());
        }
    }, [isNordVPNAvailable]);
    return <NordVPNCard isNordVPNAvailable={isNordVPNAvailable}/>;
};
const FeedFraudDetectionSection = () => <FraudDetectionCard />;
const sections = [NordVPNSection, FeedFraudDetectionSection];
const strings = {
    header: {
        title: 'Settings',
    },
    privacyToolkit: 'Your privacy toolkit',
};
const keyExtractor = (item) => item.name;
const renderItem = ({ item: Section }) => <Section />;
const ItemSeparator = () => <Spacer h={16}/>;
export const AccountSettingsScreen = withAnimatedNavigationHeader(({ scrollY, navigation }) => {
    const userId = useAppSelector((state) => state.user.user.id);
    const showSubscriptionPaymentFailed = useAppSelector(selectShowSubscriptionPaymentFailed);
    const showSeatBasedSubscriptionPaymentFailed = useAppSelector(selectShowSeatBasedSubscriptionPaymentFailed);
    const showWalkthroughV6 = useShowWalkthroughV6();
    const isMainWalkthroughV6Active = useIsMainAppWalkthroughV6Active();
    const onOpenSettings = useCallback(() => {
        navigation.navigate('Settings');
    }, []);
    const onOpenHelp = useCallback(() => {
        navigation.navigate('Help', { articlesKey: HELP_ARTICLES_KEY.SETTINGS }, { pop: true });
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => isMainWalkthroughV6Active ? undefined : (<>
              <NavigationHeaderRightIconButton onPress={onOpenSettings}>
                <IconSettings colorKey="primary"/>
              </NavigationHeaderRightIconButton>
              <Spacer.W8 />
              <NavigationHeaderRightIconButton onPress={onOpenHelp}>
                <IconHelp colorKey="primary"/>
              </NavigationHeaderRightIconButton>
            </>),
        });
    }, [isMainWalkthroughV6Active, onOpenHelp, onOpenSettings]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const { itemPositions, scrollHeight, scrollLayoutHeight, onLayout: onLayoutForWalkthrough, onContentSizeChange, } = useWalkthroughLayout();
    const onLayoutQuests = useCallback((event) => {
        onLayoutForWalkthrough('quests', event);
    }, [onLayoutForWalkthrough]);
    const onLayoutLearn = useCallback((event) => {
        onLayoutForWalkthrough('learn', event);
    }, [onLayoutForWalkthrough]);
    const ListHeaderComponent = useMemo(() => (<>
          {showSubscriptionPaymentFailed ? (<SubscriptionPaymentFailed withMarginBottom={!showSeatBasedSubscriptionPaymentFailed}/>) : null}
          {showSeatBasedSubscriptionPaymentFailed ? (<SeatBasedSubscriptionPaymentFailed withMarginBottom/>) : null}
          <AccountSettingsHeader onLayoutQuests={onLayoutQuests} onLayoutLearn={onLayoutLearn}/>
          <SectionHeader horizontalMargins largeVerticalMargins>
            {strings.privacyToolkit}
          </SectionHeader>
        </>), [
        showSubscriptionPaymentFailed,
        showSeatBasedSubscriptionPaymentFailed,
        onLayoutQuests,
        onLayoutLearn,
    ]);
    const onLayoutList = useCallback((event) => {
        onContentSizeChange(event.nativeEvent.layout.width, event.nativeEvent.layout.height);
    }, [onContentSizeChange]);
    if (userId === -1) {
        return (<AppView type="secondary" withFrame={false}>
          <Indicator dark/>
        </AppView>);
    }
    return (<AppView type="secondary" withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <View style={styles.flex} onLayout={onLayoutList}>
          <Animated.FlatList data={sections} scrollEventThrottle={16} renderItem={renderItem} onScroll={scrollHandler} keyExtractor={keyExtractor} ItemSeparatorComponent={ItemSeparator} ListHeaderComponentStyle={styles.header} ListHeaderComponent={ListHeaderComponent} contentContainerStyle={contentContainerStyle}/>
        </View>
        {!!showWalkthroughV6 && (<Walkthrough scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>)}
      </AppView>);
}, ({ colors, route }) => ({
    title: strings.header.title,
    hideTitleOnScroll: true,
    Overlay: () => <WalkthroughHeaderOverlay v6/>,
    headerTintColor: route.params?.headerTintColor,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    header: {
        paddingTop: rem(16),
    },
    flex: {
        flex: 1,
    },
});
